import React, { Dispatch, FunctionComponent, useState } from "react";
import CustomError from "./CustomError";

/* Validation */
import { Formik, useFormikContext } from "formik";
import { validationSchema } from "./formConfig";

/* API */
import axios from "axios";
import { Endpoints } from "@internal/common";
import { Button, Flex, Text, Title } from "@gjensidige/builders-components";
import FormInput from "./components/FormInput";
import FormTextArea from "./components/FormTextArea";
import FormInputGroup from "./components/FormInputGroup";
import { ClaimReportMachineFormData } from "./ClaimModels";
import FormAlert from "./components/FormAlert";

export interface IProps {
  claimType: string;
}

interface FormData extends ClaimReportMachineFormData {
  claimType: string;
}

const ClaimReportMachine: React.FC<IProps> = (props) => {
  const [formData, setFormData] = useState({
    claimType: props.claimType,
    cvrNumber: "",
    policyNumber: "",
    contactPersonName: "",
    email: "",
    contactPersonPhoneNumber: "",
    damageAddress: "",
    machineBelongsToCompany: "",
    machineBelongsTo: "",
    damageDate: "",
    product: "",
    productType: "",
    productChassisNumber: "",
    productYearModel: "",
    productPositionNumberOnPolicy: "",
    damageDetailsAndCause: "",
    machineCaretakerDamageOccured: "",
    machineRepairOnSiteOrSpecialWorkshop: "",
    whoRepairsMachine: "",
    machinePreviouslyRepaired: "",
    insuredByOthers: "",
    insuranceCompanyNameAndAmount: "",
    estimateRepairTime: "",
    workshopNameAndAddress: "",
    workshopPriceEstimate: "",
    ownPriceEstimate: "",
    consent: false,
  });
  const [submitted, setSubmitted] = useState(false);
  const [apiError, setApiError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const sendFormData = () => {
    setApiError(false);
    setIsLoading(true);
    axios
      .post(Endpoints.ClaimReportDIByggeri, formData)
      .then(() => setSubmitted(true))
      .catch(() => {
        setIsLoading(false);
        setApiError(true);
      });
  };

  return (
    <>
      {submitted ? (
        <FormAlert
          severity="success"
          title="Tak for din anmeldelse"
          text="Vi sender dig en kvitteringsmail med informationerne fra din skadeanmeldelse."
        />
      ) : (
        <>
          <Title size={5}>Anmeldelse af skade på maskine</Title>
          <Text>* Påkrævede felter</Text>
          <Formik
            enableReinitialize
            initialValues={{
              cvrNumber: formData.cvrNumber,
              policyNumber: formData.policyNumber,
              contactPersonName: formData.contactPersonName,
              email: formData.email,
              contactPersonPhoneNumber: formData.contactPersonPhoneNumber,
              damageAddress: formData.damageAddress,
              machineBelongsToCompany: formData.machineBelongsTo,
              machineBelongsTo: formData.machineBelongsTo,
              damageDate: formData.damageDate,
              product: formData.product,
              productType: formData.productType,
              productChassisNumber: formData.productChassisNumber,
              productYearModel: formData.productYearModel,
              productPositionNumberOnPolicy:
                formData.productPositionNumberOnPolicy,
              damageDetailsAndCause: formData.damageDetailsAndCause,
              machineCaretakerDamageOccured:
                formData.machineCaretakerDamageOccured,
              machineRepairOnSiteOrSpecialWorkshop:
                formData.machineRepairOnSiteOrSpecialWorkshop,
              whoRepairsMachine: formData.whoRepairsMachine,
              machinePreviouslyRepaired: formData.machinePreviouslyRepaired,
              insuredByOthers: formData.insuredByOthers,
              insuranceCompanyNameAndAmount:
                formData.insuranceCompanyNameAndAmount,
              estimateRepairTime: formData.estimateRepairTime,
              workshopNameAndAddress: formData.workshopNameAndAddress,
              workshopPriceEstimate: formData.workshopPriceEstimate,
              ownPriceEstimate: formData.ownPriceEstimate,
              consent: formData.consent,
            }}
            validationSchema={validationSchema(formData)}
            onSubmit={() => {
              sendFormData();
            }}
          >
            <RenderForm isLoading={isLoading} setFormData={setFormData} />
          </Formik>
        </>
      )}
      {apiError && (
        <FormAlert
          severity="error"
          text="Der skete en fejl ved indsendelse. Prøv venligst igen senere"
          title="Der skete en fejl."
        />
      )}
    </>
  );
};

interface RenderProps {
  isLoading: boolean;
  setFormData: Dispatch<React.SetStateAction<FormData>>;
}

const RenderForm: FunctionComponent<RenderProps> = ({
  isLoading,
  setFormData,
}) => {
  const {
    handleSubmit,
    handleBlur,
    setFieldValue,
    touched,
    errors,
    values: formData,
  } = useFormikContext<FormData>();

  return (
    <form onSubmit={handleSubmit}>
      <Flex layout={1}>
        <FormInput
          id="cvr-number"
          labelText="CVR-nummer *"
          name="cvrNumber"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("cvrNumber", event.target.value);
            setFormData({
              ...formData,
              cvrNumber: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.cvrNumber && errors.cvrNumber)}
          errorText={errors.cvrNumber}
          value={formData.cvrNumber}
        />
        <FormInput
          id="policy-number"
          labelText="Policenummer *"
          name="policyNumber"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("policyNumber", event.target.value);
            setFormData({
              ...formData,
              policyNumber: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.policyNumber && errors.policyNumber)}
          errorText={errors.policyNumber}
          value={formData.policyNumber}
        />
        <FormInput
          id="contactPersonName"
          labelText="Navn (kontaktperson) *"
          name="contactPersonName"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("contactPersonName", event.target.value);
            setFormData({
              ...formData,
              contactPersonName: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.contactPersonName && errors.contactPersonName)}
          errorText={errors.contactPersonName}
          value={formData.contactPersonName}
        />
        <FormInput
          id="email"
          labelText="Email (kontaktperson) *"
          name="email"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("email", event.target.value);
            setFormData({
              ...formData,
              email: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.email && errors.email)}
          errorText={errors.email}
          value={formData.email}
        />
        <FormInput
          id="contactPersonPhoneNumber"
          labelText="Telefonnummer (kontaktperson) *"
          name="contactPersonPhoneNumber"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("contactPersonPhoneNumber", event.target.value);
            setFormData({
              ...formData,
              contactPersonPhoneNumber: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={
            !!(
              touched.contactPersonPhoneNumber &&
              errors.contactPersonPhoneNumber
            )
          }
          errorText={errors.contactPersonPhoneNumber}
          value={formData.contactPersonPhoneNumber}
        />
        <FormTextArea
          id="damageAddress"
          labelText="Hvor er skaden sket? *"
          placeholder="Oplys vejnavn og nummer, by samt postnummer"
          maxLength={500}
          value={formData.damageAddress}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
            setFieldValue("damageAddress", event.target.value);
            setFormData({
              ...formData,
              damageAddress: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.damageAddress && errors.damageAddress)}
          errorText={errors.damageAddress}
        />
        <FormInputGroup
          labelText="Tilhører den beskadigede maskine din virksomhed? *"
          id="machineBelongsToCompany"
          onChange={(event) => {
            setFormData({
              ...formData,
              machineBelongsToCompany: event.at(0) ?? "",
            });
            setFieldValue("machineBelongsToCompany", event.at(0) ?? "");
          }}
          options={[
            {
              label: "Ja",
              value: "Ja",
            },
            {
              label: "Nej",
              value: "Nej",
            },
          ]}
          type="radio"
          value={formData.machineBelongsToCompany}
          onBlur={handleBlur}
          invalid={
            !!(
              touched.machineBelongsToCompany && errors.machineBelongsToCompany
            )
          }
          errorText={errors.machineBelongsToCompany}
        />
        {formData.machineBelongsToCompany === "Nej" && (
          <FormInput
            id="machineBelongsTo"
            labelText="Hvem tilhører maskinen? *"
            name="machineBelongsTo"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setFieldValue("machineBelongsTo", event.target.value);
              setFormData({
                ...formData,
                machineBelongsTo: event.target.value,
              });
            }}
            onBlur={handleBlur}
            invalid={!!(touched.machineBelongsTo && errors.machineBelongsTo)}
            errorText={errors.machineBelongsTo}
            value={formData.machineBelongsTo}
          />
        )}
        <FormInput
          id="damageDate"
          labelText="Hvornår skete skaden? *"
          placeholder="Dato og klokkeslæt/tidsrum"
          name="damageDate"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("damageDate", event.target.value);
            setFormData({
              ...formData,
              damageDate: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.damageDate && errors.damageDate)}
          errorText={errors.damageDate}
          value={formData.damageDate}
        />

        <Title size={5}>Den skadesramte maskine</Title>

        <FormInput
          id="product"
          labelText="Fabrikat *"
          name="product"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("product", event.target.value);
            setFormData({
              ...formData,
              product: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.product && errors.product)}
          errorText={errors.product}
          value={formData.product}
        />
        <FormInput
          id="productType"
          labelText="Type *"
          name="productType"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("productType", event.target.value);
            setFormData({
              ...formData,
              productType: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.productType && errors.productType)}
          errorText={errors.productType}
          value={formData.productType}
        />
        <FormInput
          id="productChassisNumber"
          labelText="Stelnummer *"
          name="productChassisNumber"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("productChassisNumber", event.target.value);
            setFormData({
              ...formData,
              productChassisNumber: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={
            !!(touched.productChassisNumber && errors.productChassisNumber)
          }
          errorText={errors.productChassisNumber}
          value={formData.productChassisNumber}
        />
        <FormInput
          id="productYearModel"
          labelText="Årgang *"
          name="productYearModel"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("productYearModel", event.target.value);
            setFormData({
              ...formData,
              productYearModel: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.productYearModel && errors.productYearModel)}
          errorText={errors.productYearModel}
          value={formData.productYearModel}
        />
        <FormInput
          id="productPositionNumberOnPolicy"
          labelText="Positionsnr. på policen *"
          name="productPositionNumberOnPolicy"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("productPositionNumberOnPolicy", event.target.value);
            setFormData({
              ...formData,
              productPositionNumberOnPolicy: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={
            !!(
              touched.productPositionNumberOnPolicy &&
              errors.productPositionNumberOnPolicy
            )
          }
          errorText={errors.productPositionNumberOnPolicy}
          value={formData.productPositionNumberOnPolicy}
        />
        <FormTextArea
          id="damageDetailsAndCause"
          labelText="Hvori består skaden og hvad var årsagen? *"
          maxLength={500}
          value={formData.damageDetailsAndCause}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
            setFieldValue("damageDetailsAndCause", event.target.value);
            setFormData({
              ...formData,
              damageDetailsAndCause: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={
            !!(touched.damageDetailsAndCause && errors.damageDetailsAndCause)
          }
          errorText={errors.damageDetailsAndCause}
        />
        <FormInput
          id="machineCaretakerDamageOccured"
          labelText="Hvem passede maskinen, da skaden indtraf? *"
          name="machineCaretakerDamageOccured"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("machineCaretakerDamageOccured", event.target.value);
            setFormData({
              ...formData,
              machineCaretakerDamageOccured: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={
            !!(
              touched.machineCaretakerDamageOccured &&
              errors.machineCaretakerDamageOccured
            )
          }
          errorText={errors.machineCaretakerDamageOccured}
          value={formData.machineCaretakerDamageOccured}
        />
        <FormInput
          id="machineRepairOnSiteOrSpecialWorkshop"
          labelText="Kan maskinen repareres på stedet eller skal dele af den på specialværksted? *"
          name="machineRepairOnSiteOrSpecialWorkshop"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue(
              "machineRepairOnSiteOrSpecialWorkshop",
              event.target.value,
            );
            setFormData({
              ...formData,
              machineRepairOnSiteOrSpecialWorkshop: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={
            !!(
              touched.machineRepairOnSiteOrSpecialWorkshop &&
              errors.machineRepairOnSiteOrSpecialWorkshop
            )
          }
          errorText={errors.machineRepairOnSiteOrSpecialWorkshop}
          value={formData.machineRepairOnSiteOrSpecialWorkshop}
        />
        <FormInput
          id="whoRepairsMachine"
          labelText="Hvem skal foretage reparationen? *"
          name="whoRepairsMachine"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("whoRepairsMachine", event.target.value);
            setFormData({
              ...formData,
              whoRepairsMachine: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.whoRepairsMachine && errors.whoRepairsMachine)}
          errorText={errors.whoRepairsMachine}
          value={formData.whoRepairsMachine}
        />
        <FormInputGroup
          labelText="Har maskinen tidligere været repareret? *"
          id="machinePreviouslyRepaired"
          onChange={(event) => {
            setFormData({
              ...formData,
              machinePreviouslyRepaired: event.at(0) ?? "",
            });
            setFieldValue("machinePreviouslyRepaired", event.at(0) ?? "");
          }}
          options={[
            {
              label: "Ja",
              value: "Ja",
            },
            {
              label: "Nej",
              value: "Nej",
            },
          ]}
          type="radio"
          value={formData.machinePreviouslyRepaired}
          onBlur={handleBlur}
          invalid={
            !!(
              touched.machinePreviouslyRepaired &&
              errors.machinePreviouslyRepaired
            )
          }
          errorText={errors.machinePreviouslyRepaired}
        />
        <FormInputGroup
          labelText="Er der tegnet forsikring mod samme skade i et andet selskab? *"
          id="insuredByOthers"
          onChange={(event) => {
            setFormData({
              ...formData,
              insuredByOthers: event.at(0) ?? "",
            });
            setFieldValue("insuredByOthers", event.at(0) ?? "");
          }}
          options={[
            {
              label: "Ja",
              value: "Ja",
            },
            {
              label: "Nej",
              value: "Nej",
            },
          ]}
          type="radio"
          value={formData.insuredByOthers}
          onBlur={handleBlur}
          invalid={!!(touched.insuredByOthers && errors.insuredByOthers)}
          errorText={errors.insuredByOthers}
        />
        {formData.insuredByOthers === "Ja" && (
          <FormInput
            id="insuranceCompanyNameAndAmount"
            labelText="Hvilket selskab og for hvilken sum? *"
            name="insuranceCompanyNameAndAmount"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setFieldValue(
                "insuranceCompanyNameAndAmount",
                event.target.value,
              );
              setFormData({
                ...formData,
                insuranceCompanyNameAndAmount: event.target.value,
              });
            }}
            onBlur={handleBlur}
            invalid={
              !!(
                touched.insuranceCompanyNameAndAmount &&
                errors.insuranceCompanyNameAndAmount
              )
            }
            errorText={errors.insuranceCompanyNameAndAmount}
            value={formData.insuranceCompanyNameAndAmount}
          />
        )}
        <FormInput
          id="estimateRepairTime"
          labelText="Hvor længe antages reparationen at tage? *"
          name="estimateRepairTime"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("estimateRepairTime", event.target.value);
            setFormData({
              ...formData,
              estimateRepairTime: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.estimateRepairTime && errors.estimateRepairTime)}
          errorText={errors.estimateRepairTime}
          value={formData.estimateRepairTime}
        />

        <Title size={5}>Reparation af skaden</Title>

        <FormTextArea
          id="workshopNameAndAddress"
          labelText="Værkstedets navn og adresse *"
          maxLength={500}
          value={formData.workshopNameAndAddress}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
            setFieldValue("workshopNameAndAddress", event.target.value);
            setFormData({
              ...formData,
              workshopNameAndAddress: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={
            !!(touched.workshopNameAndAddress && errors.workshopNameAndAddress)
          }
          errorText={errors.workshopNameAndAddress}
        />
        <FormInput
          id="workshopPriceEstimate"
          labelText="Værkstedets prisoverslag *"
          name="workshopPriceEstimate"
          helpText="Husk at noter om prisen er inkl. eller ekskl. moms"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("workshopPriceEstimate", event.target.value);
            setFormData({
              ...formData,
              workshopPriceEstimate: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={
            !!(touched.workshopPriceEstimate && errors.workshopPriceEstimate)
          }
          errorText={errors.workshopPriceEstimate}
          value={formData.workshopPriceEstimate}
        />
        <FormInput
          id="ownPriceEstimate"
          labelText="Evt. eget overslag *"
          name="ownPriceEstimate"
          helpText="Husk at noter om prisen er inkl. eller ekskl. moms"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("ownPriceEstimate", event.target.value);
            setFormData({
              ...formData,
              ownPriceEstimate: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.ownPriceEstimate && errors.ownPriceEstimate)}
          errorText={errors.ownPriceEstimate}
          value={formData.ownPriceEstimate}
        />

        <FormInput
          id="consent-id"
          labelText="Jeg erklærer herved, at foranstående oplysninger er i overensstemmelse med de faktiske forhold, og at jeg vil yde selskabet enhver assistance i forbindelse med skaden. *"
          name="consent"
          type="checkbox"
          value="consent"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("consent", event.target.checked);
            setFormData({
              ...formData,
              consent: event.target.checked,
            });
          }}
          onBlur={handleBlur}
          checked={formData.consent}
        />
        {touched.consent && errors.consent && (
          <>
            <br />
            <CustomError errorText={errors.consent} />
          </>
        )}
        <Button loading={isLoading} type="submit" flexible>
          Send
        </Button>
      </Flex>
    </form>
  );
};

export default ClaimReportMachine;
